import { ViewportScroller, isPlatformBrowser } from '@angular/common';
import {
  ApplicationRef,
  ComponentRef,
  Inject,
  Injectable,
  Injector,
  OnDestroy,
  PLATFORM_ID,
} from '@angular/core';
import { EventType, NavigationEnd, NavigationSkipped, Router, Scroll } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter, pairwise } from 'rxjs/operators';
import { CustomOnNavigateConfig } from './custom-on-navigate-config';

@Injectable({
  providedIn: 'root',
})
export class CustomOnNavigateService implements OnDestroy {
  protected subscriptions: Subscription = new Subscription();

  get hostComponent(): ComponentRef<any> {
    return this.injector.get(ApplicationRef)?.components?.[0];
  }

  constructor(
    protected config: CustomOnNavigateConfig,
    protected router: Router,
    protected viewportScroller: ViewportScroller,
    protected injector: Injector,
    @Inject(PLATFORM_ID) private platformId: any
  ) { }

  /**
   * Reads configuration and enables features based on flags set.
   */
  initializeWithConfig(): void {
    if (this.config?.customEnableResetViewOnNavigate?.active) {
      this.setResetViewOnNavigate(this.config.customEnableResetViewOnNavigate.active);
    }
  }

  /**
   * Resets view back to the original position when performing a back navigation and to the top when performing a front navigation
   * and sets the focus back to the top of the page before skiplinks for any type of navigation
   * @param enable Enable or disable this feature
   */
  setResetViewOnNavigate(enable: boolean): void {
    if (enable) {
      this.subscriptions.add(this.router.events
        .pipe(
          filter((event): event is Scroll => event instanceof Scroll),
          pairwise()
        )
        .subscribe((event) => {
          const previousRoute = event[0];
          const currentRoute = event[1];

          if (currentRoute.position) {
            // allow the pages to be repainted before scrolling to proper position
            if (isPlatformBrowser(this.platformId)) {
              this.scrollToPosition(currentRoute.position)
            }
          } else {
            if (
              this.config.customEnableResetViewOnNavigate?.ignoreQueryString &&
              this.isPathEqual(previousRoute, currentRoute)
            ) {
              return;
            }

            if (this.isChildRoute(currentRoute)) {
              return;
            }
          }

          this.hostComponent?.location?.nativeElement.focus();
        }));
    }
  }

  /**
   * Verifies if the current route is a child route from the given ignore config route
   *
   * @param route
   * @returns boolean whether the route is a child route
   */
  private isChildRoute(route: Scroll): boolean {
    return (
      this.config.customEnableResetViewOnNavigate?.ignoreRoutes?.some((configRoute) =>
        this.isNavigationEnd(route.routerEvent) &&
        route.routerEvent.urlAfterRedirects.split('/').includes(configRoute)
      ) ?? false
    );
  }

  /**
   * Verifies if the previous and current route are the same without the query string
   *
   * @param previousRoute
   * @param currentRoute
   * @returns boolean depending on the previous and current route are equal without the query strings
   */
  private isPathEqual(previousRoute: Scroll, currentRoute: Scroll): boolean {
    return (
      this.isNavigationEnd(previousRoute.routerEvent) &&
      this.isNavigationEnd(currentRoute.routerEvent) &&
      previousRoute.routerEvent.urlAfterRedirects.split('?')[0] ===
      currentRoute.routerEvent.urlAfterRedirects.split('?')[0]
    );
  }

  /**
   * Scrolls to a specified position based on the current route and configuration.
   * @param currentRoute The current route containing scroll information.
   * @param position The target scroll position as [x, y] coordinates, or null.
   */
  private scrollToPosition(position: [number, number] | null): void {
    const timerId = setTimeout(() => {
      this.viewportScroller.scrollToPosition(position); 
      clearTimeout(timerId);
    }, 300);
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  isNavigationEnd(
    event: NavigationEnd | NavigationSkipped
  ): event is NavigationEnd {
    return event.type === EventType.NavigationEnd;
  }
}
